import { useMemo } from 'react'
import throttle from 'lodash.throttle'
import { MRT_ColumnDef } from 'material-react-table'
import { Chip } from '@mui/material'
import useDocumentGrid from '@/hooks/useDocumentGrid'
import useDocumentIdOverlay from '@/hooks/useDocumentIdOverlay'
import useGetThemeColor from '@/hooks/useGetThemeColor'
import useGetRelevantWorkflowState from '@/hooks/useRelevantWorkflowState'
import useValidationNavList from '@/hooks/useValidationNavList'
import { DocumentRow } from '@/types/documents'
import { Project } from '@/types/projects'
import { WorkflowState } from '@/types/workflows'
import { prettifyDate } from '@/utils/time'
import FieldCell from '@/components/document-table/FieldCell'
import { useNotifications } from '@/components/notifications/NotificationProvider'
import InfiniteScrollTable from '@/components/zerapix-table/InfiniteScrollTable'
import useInfiniteScrollZerapixTable from '@/components/zerapix-table/useInfiniteScrollZerapixTable'
import DocumentOverlay from './document-viewer/DocumentOverlay'

type DocumentsTableProps = {
  project: Project
  workflowId: string
}
export default function DocumentsTable({
  project,
  workflowId,
}: DocumentsTableProps) {
  const documentOverlay = useDocumentIdOverlay()
  const { documentIdState, overlay } = documentOverlay
  const getRelevantWorkflowState = useGetRelevantWorkflowState()
  const getThemeColor = useGetThemeColor()

  const fieldsInLiteMode = useMemo(
    () =>
      project.project_grids
        ?.flatMap((grid) => grid.project_grid_fields)
        .filter((field) => field.is_lite_column) || [],
    [project.project_grids],
  )

  const gridIds = fieldsInLiteMode
    .map((field) => field.project_grid_id)
    .filter((id) => id)

  const query = useDocumentGrid({
    projectId: project.id,
    projectGridIds: gridIds,
    queryBy: gridIds.length > 0 ? 'rows' : 'documents',
    ordering: ['workflowStateColId', 'uploadColId'],
    workflowId,
  })

  const {
    rows,
    rowsUniqueDocIds,
    fetchNextPage,
    hasNextPage,
    isFetching,
    refetch,
  } = query

  const navListQuery = useValidationNavList({
    currentDocumentId: documentIdState.value,
    documentIds: rowsUniqueDocIds,
    setDocumentId: documentIdState.toggleValue,
    rowsCount: rows.length,
    fetchNextPage:
      hasNextPage && !isFetching && overlay.isOpen ? fetchNextPage : undefined,
    onClose: () => {
      documentOverlay.closeDocument()
      refetch()
    },
  })

  const columns = useMemo<MRT_ColumnDef<Required<DocumentRow>>[]>(() => {
    const fieldColumns: MRT_ColumnDef<Required<DocumentRow>>[] =
      fieldsInLiteMode.map((field) => ({
        id: field.id,
        accessorFn: (row) =>
          row.document_row_values?.find((value) => {
            return value.project_grid_field_id === field.id
          })?.final_value,
        header: field.name,
        Cell: (props) => {
          return <FieldCell field={field} {...props} />
        },
      }))
    return [
      {
        id: 'fileColId',
        accessorFn: (row) => row.document.name,
        header: 'Document',
      },
      {
        id: 'uploadColId',
        accessorFn: (row) => prettifyDate(row.document.uploaded_at),
        header: 'Upload Date',
      },
      ...fieldColumns,
      {
        id: 'workflowStateColId',
        accessorFn: (row) => getRelevantWorkflowState(row.document),
        Cell: ({ cell }) => {
          const workflowState = cell.getValue() as WorkflowState
          return (
            <Chip
              label={workflowState?.name || 'No Status'}
              size="small"
              sx={{
                mr: 1,
                background: getThemeColor(workflowState?.color),
                color: 'white',
              }}
            />
          )
        },
        header: 'Status',
        muiTableHeadCellProps: {
          sx: {
            borderColor: (theme) => theme.palette.divider, // match default color set in useZerapixTable
            '& .Mui-TableHeadCell-Content': {
              justifyContent: 'flex-end !important',
              pr: 1.5,
            },
          },
        },
        muiTableBodyCellProps: {
          sx: {
            borderColor: (theme) => theme.palette.divider, // match default color set in useZerapixTable
            justifyContent: 'flex-end !important',
          },
        },
      },
    ]
  }, [fieldsInLiteMode, getRelevantWorkflowState, getThemeColor])

  const columnOrder = useMemo(() => {
    return columns.map((col) => col.id as string)
  }, [columns])

  const throttledRefetch = throttle(query.refetch, 1000)
  const table = useInfiniteScrollZerapixTable<Required<DocumentRow>>({
    query,
    columns,
    enableTopToolbar: false,
    enableBottomToolbar: true,
    data: rows as Required<DocumentRow>[],
    initialState: {
      density: 'compact',
    },
    muiTableHeadRowProps: {
      sx: {
        py: 0.5,
      },
    },
    muiTableContainerProps: {
      sx: {
        backgroundColor: (theme) =>
          theme.palette.mode === 'dark'
            ? undefined
            : theme.palette.background.paper,
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        documentOverlay.openDocument(row.original.document_id)
      },
      sx: {
        cursor: 'pointer',
      },
    }),
    state: {
      columnOrder,
    },
    muiBottomToolbarProps: {
      sx: {
        minHeight: 0,
        height: '5px', // Making it small to still get room for the loading bar
      },
    },
  })

  useNotifications({
    keys: ['document_workflow_state_changed'],
    callback: () => throttledRefetch(),
  })

  return (
    <div>
      <InfiniteScrollTable query={query} table={table} />
      <DocumentOverlay
        documentOverlay={documentOverlay}
        projectId={project.id}
        {...navListQuery}
      />
    </div>
  )
}
